import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import Icon from "./icons";

// TemizleOnay — geri dönüşsüz işlemlerin onay diyaloğu (ui-admin §5).
//
// Süperadmin burada uzun süre oturmuyor; hızlı hareket ediyor ve yanlış
// düğmeye basmak veri kaybı demek. Bu yüzden dört koruma birlikte çalışır:
//
//  1. NE KAYBOLACAĞI YAZILI — kapsam listesi diyaloğun içinde, rakamlı.
//  2. ADI YAZDIRMA — onay sözcüğü elle yazılmadan yıkıcı düğme AÇILMAZ.
//     Kaza eseri onaylamayı imkânsız kılan asıl koruma budur.
//  3. VARSAYILAN ODAK İPTALDE — diyalog açılır açılmaz Enter'a basan
//     kullanıcı vazgeçmiş olur, silmiş değil.
//  4. ENTER ONAYLAMAZ — metin kutusunda Enter yutulur; onay yalnız tıkla
//     (veya düğmeye Tab'layıp Space/Enter ile) verilir.
//
// Esc kapatır, Tab kutunun dışına çıkmaz, kapanınca odak çağıran düğmeye
// döner (AdBlockUyari ile aynı desen).
export default function TemizleOnay({
  acik,
  baslik,
  onaySozcugu = "SIL",
  eylemEtiketi,
  calisiyor = false,
  hata,
  children,
  onIptal,
  onOnay,
}: {
  acik: boolean;
  baslik: string;
  /** Kullanıcının birebir yazması gereken sözcük. Sunucudaki onayla aynı olmalı. */
  onaySozcugu?: string;
  /** Yıkıcı düğmenin metni — "Sil" değil, ne yapacağını söyleyen tam cümle. */
  eylemEtiketi: string;
  calisiyor?: boolean;
  hata?: string | null;
  /** Kapsam metni: ne kaybolacak, rakamıyla. */
  children: ReactNode;
  onIptal: () => void;
  onOnay: () => void;
}) {
  const [yazilan, setYazilan] = useState("");
  const kutuRef = useRef<HTMLDivElement>(null);
  const iptalRef = useRef<HTMLButtonElement>(null);

  const kapat = useCallback(() => {
    if (calisiyor) return; // silme sürerken kapatma: yarım durum kafa karıştırır
    onIptal();
  }, [calisiyor, onIptal]);

  // Her açılışta sıfırdan: bir önceki denemede yazılan onay sözcüğü kalırsa
  // ikinci kez tek tıkla silinebilirdi.
  useEffect(() => {
    if (acik) setYazilan("");
  }, [acik]);

  useEffect(() => {
    if (!acik) return;
    const onceki = document.activeElement as HTMLElement | null;
    // Odak İPTALDE — yıkıcı düğmede değil.
    iptalRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); kapat(); return; }
      if (e.key !== "Tab") return;
      const oge = kutuRef.current?.querySelectorAll<HTMLElement>(
        "button:not([disabled]), input:not([disabled]), [href]",
      );
      if (!oge || oge.length === 0) return;
      const ilk = oge[0];
      const son = oge[oge.length - 1];
      if (e.shiftKey && document.activeElement === ilk) { e.preventDefault(); son.focus(); }
      else if (!e.shiftKey && document.activeElement === son) { e.preventDefault(); ilk.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      onceki?.focus?.();
    };
  }, [acik, kapat]);

  if (!acik) return null;

  const onayli = yazilan.trim().toUpperCase() === onaySozcugu.toUpperCase();

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) kapat(); }}
    >
      <div
        className="modal-box"
        ref={kutuRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="temizle-onay-baslik"
      >
        <div className="modal-head">
          <span className="adblock-ico" style={{ color: "var(--err)" }}>
            <Icon name="alert" size={18} />
          </span>
          <h2 id="temizle-onay-baslik">{baslik}</h2>
        </div>

        <div className="modal-body">
          {children}

          <label className="danger-confirm">
            Onaylamak için <strong>{onaySozcugu}</strong> yazın:
            <input
              type="text"
              value={yazilan}
              onChange={(e) => setYazilan(e.target.value)}
              onKeyDown={(e) => {
                // Enter ONAYLAMAZ — yıkıcı işlemde kaza riski (ui-admin §5).
                if (e.key === "Enter") e.preventDefault();
              }}
              autoComplete="off"
              spellCheck={false}
              aria-label={`Onay sözcüğü: ${onaySozcugu}`}
              disabled={calisiyor}
            />
          </label>

          {hata && <div className="error" role="alert">{hata}</div>}
        </div>

        <div className="modal-actions">
          <button ref={iptalRef} type="button" className="btn-soft" onClick={kapat} disabled={calisiyor}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn-danger btn-danger-solid"
            onClick={onOnay}
            disabled={!onayli || calisiyor}
            title={onayli ? undefined : `Önce ${onaySozcugu} yazın`}
          >
            {calisiyor ? "Siliniyor…" : eylemEtiketi}
          </button>
        </div>
      </div>
    </div>
  );
}
